import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Box, IconButton, Typography, Alert, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, Grid
    //CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { userService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const UserFormModal = ({ open, onClose, userId, onSuccess }) => {
    const { showToast } = useToast();
    const [error, setError] = useState('');
    const [roles, setRoles] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [managers, setManagers] = useState([]);
    const [activeFields, setActiveFields] = useState([]);
    const [jobRoles, setJobRoles] = useState([]);
    const [hasManagerRole, setHasManagerRole] = useState(false)

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        roles: [],
        admin_id: '',
        manager_id: '',
        audience_type: '',
        //gender: 'Male',
        active: true,
        job_role_id: ''
    });

    const [fieldValues, setFieldValues] = useState({});
    const [showAdminField, setShowAdminField] = useState(false);

    const fetchFormData = useCallback(async () => {
        try {
            const rolesResponse = await userService.getRoles();
            if (rolesResponse.data.success) {
                setRoles(rolesResponse.data.roles);
            }
            
            // Fetch accounts (admin users)
            const accountsResponse = await userService.getUsers(1, 100, '');
            if (accountsResponse.data.success) {
                const adminUsers = accountsResponse.data.users.filter(user => 
                    user.roles.includes('Admin') || user.roles.includes('Account Admin')
                );
                setAccounts(adminUsers.map(user => ({
                    id: user.id,
                    admin_name: `${user.firstName} ${user.lastName}`
                })));
            }
            
            const managersResponse = await userService.getUsers(1, 100, '');
            if (managersResponse.data.success) {
                const managerUsers = managersResponse.data.users.filter(user => 
                    user.roles.includes('Manager')
                );
                setManagers(managerUsers.map(user => ({
                    id: user.id,
                    first_name: user.firstName,
                    last_name: user.lastName
                })));
            }
            
            try {
                const jobRolesResponse = await userService.getJobRoles();
                if (jobRolesResponse.data.success) {
                    setJobRoles(jobRolesResponse.data.jobRoles);
                }
            } catch (error) {
                console.error('Error fetching job roles:', error);
                setJobRoles([]);
            }

            try {
                const activeFieldsResponse = await fetch('/api/active-fields/api');
                const activeFieldsData = await activeFieldsResponse.json();
                if (activeFieldsData.success) {
                    setActiveFields(activeFieldsData.fields);
                }
            } catch (error) {
                console.error('Error fetching active fields:', error);
                // Set empty active fields if the endpoint doesn't exist yet
                setActiveFields([]);
            }
        } catch (error) {
            console.error('Error fetching form data:', error);
            setError('Failed to load form data');
        }
    }, []);

    const fetchUserData = useCallback(async () => {
        try {
            const response = await userService.getUserDetails(userId);
            // Mock user data for demonstration
            if (response.data.success) {
                const userData = response.data.userData;
                
                // Extract role IDs from the roles string
                const userRoles = await userService.getUserRoles(userId);
                const roleIds = userRoles.data.success ? 
                    userRoles.data.roles.map(role => role.id.toString()) : [];
                
                setFormData({
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    email: userData.email,
                    username: userData.username,
                    roles: roleIds,
                    audience_type: userData.audience_type ? userData.audience_type : '',
                    admin_id: userData.admin_id ? userData.admin_id.toString() : '',
                    manager_id: userData.manager_id ? userData.manager_id.toString() : '',
                    //gender: userData.gender || 'Male',
                    active: userData.active === 1,
                    job_role_id: userData.job_role_id ? userData.job_role_id.toString() : ''
                });
                
                // Set active field values if they exist
                if (userData.activeFields && userData.activeFields.length > 0) {
                    const fieldValueMap = {};
                    userData.activeFields.forEach(field => {
                        // Parse field values based on whether it's multi-valued
                        if (field.is_multivalued) {
                            fieldValueMap[field.id] = field.field_values.split(',').map(val => val.trim());
                        } else {
                            fieldValueMap[field.id] = field.field_values;
                        }
                    });
                    setFieldValues(fieldValueMap);
                }
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setError('Failed to load user data');
        } 
    }, [userId]);

    const resetForm = useCallback(() => {
        const learnerRole = roles.find(role => role.name === 'learner');
        const learnerRoleId = learnerRole ? learnerRole.id.toString() : '2';
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            username: '',
            roles: [learnerRoleId],
            audience_type: '',
            admin_id: '',
            manager_id: '',
            //gender: 'Male',
            active: true,
            job_role_id: ''
        });
        setFieldValues({});
        setError('');
    }, [roles]);

    useEffect(() => {
        if (open) {
            fetchFormData();
        }
    }, [open, fetchFormData]);

    useEffect(() => {
        if (open && userId) {
            fetchUserData();
        } else if (open) {
            resetForm();
        }
    }, [open, userId, fetchUserData, resetForm]);

    useEffect(() => {
        if (roles.length > 0 && open && !userId) {
            const learnerRole = roles.find(role => role.name === 'learner' || role.description === 'Learner');
            const learnerRoleId = learnerRole ? learnerRole.id.toString() : '2'; // Default to '2' if not found
            
            if (!formData.roles.includes(learnerRoleId)) {
                setFormData(prevData => ({
                    ...prevData,
                    roles: [...prevData.roles, learnerRoleId]
                }));
            }
        }

        if (roles.length > 0 && formData.roles.length > 0) {
            const managerRole = roles.find(role => 
                role.name === 'Manager' || role.description === 'Manager');
            
            if (managerRole && formData.roles.includes(managerRole.id.toString())) {
                setHasManagerRole(true);
            } else {
                setHasManagerRole(false);
            }
        }
    }, [roles, open, userId, formData.roles]);

    useEffect(() => {
        const hasNonLearnerRole = formData.roles.some(roleId => {
            const role = roles.find(r => r.id.toString() === roleId);
            return role && role.name !== 'learner';
        });
        setShowAdminField(hasNonLearnerRole);
    }, [formData.roles, roles]);


    // Internal vs External is inferred from the email's domain — never set
    // directly by the admin. Confirm the actual internal domain(s) for your
    // workspace before shipping this. 
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value            
        }));
    };

    const handleRoleChange = (event) => {
        const { value } = event.target;
        if (!value.includes('2')) {
            setFormData({
                ...formData,
                roles: [...value, '2']
            });
        } else {
            setFormData({
                ...formData,
                roles: value
            });
        }
    };

    const handleFieldValueChange = (fieldId, value) => {
        setFieldValues({
            ...fieldValues,
            [fieldId]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        let updatedRoles = [...formData.roles];
        if (!updatedRoles.includes('2')) {
            updatedRoles.push('2');
        }

        //if (!formData.first_name || !formData.last_name || !formData.email || !formData.username || formData.roles.length === 0) {
        if (!formData.first_name || !formData.last_name || !formData.email) {
            setError('Please fill in all required fields');
            return;
        }
        
        try {
            setError('');
            
            // Prepare data for submission
            const userData = {
                ...formData,
                roles: updatedRoles,
                active: formData.active ? 1 : 0
            };
            
            // Add active field values
            Object.keys(fieldValues).forEach(fieldId => {
                userData[`active_field_${fieldId}`] = fieldValues[fieldId];
            });
            
            let response;
            if (userId) {
                response = await userService.updateUser(userId, userData);
            } else {
                response = await userService.createUser(userData);
            }
            
            if (response.data.status === 'ok') {
                showToast(
                    userId ? 'User updated successfully' : 'User created successfully',
                    'success'
                );
                if (onSuccess) onSuccess();
                onClose();
            }
        } catch (error) {
            console.error('Error saving user:', error);
            setError(error.response?.data?.message || 'Failed to save user');
        } 
    };

    // Helper to map audience_type codes to a human friendly label
    const getAudienceLabel = (type) => {
        if (type === 1 || type === '1') return 'Internal';
        if (type === 2 || type === '2') return 'External';
        return '';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{userId ? 'Edit User' : 'Invite User'}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <form
            onSubmit={handleSubmit}
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                minHeight: 0,
            }}
            >
                <DialogContent
                    dividers
                    sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    pt: 2,
                    }}
                >
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="First Name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Last Name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                disabled={!!userId} // Disable email field when editing
                            />
                        </Grid>                        
                        {/* Read-only Audience field (not editable) */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Audience"
                                name="audience_type"
                                value={getAudienceLabel(formData.audience_type)}
                                fullWidth
                                disabled
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        {/*
                        <Grid item xs={6}>
                            <FormControl fullWidth margin="normal" size="small" required>
                                <InputLabel id="roles-label">Roles</InputLabel>
                                <Select
                                    labelId="roles-label"
                                    multiple
                                    value={formData.roles}
                                    onChange={handleRoleChange}
                                    input={<OutlinedInput label="Roles" />}
                                    renderValue={(selected) => {
                                        return selected.map(roleId => {
                                            const role = roles.find(r => r.id.toString() === roleId);
                                            return role ? role.description : '';
                                        }).join(', ');
                                    }}
                                >
                                    {roles.map((role) => (
                                        <MenuItem key={role.id} value={role.id.toString()}
                                        disabled={role.name === 'learner' && formData.roles.includes(role.id.toString())}
                                        >
                                            {role.description}
                                            
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        */}
                        {/* {showAdminField && (
                            <Grid item xs={12} sm={6} className="admin-section">
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="admin-label">Account</InputLabel>
                                    <Select
                                        labelId="admin-label"
                                        name="admin_id"
                                        value={formData.admin_id}
                                        onChange={handleInputChange}
                                        label="Account"
                                    >
                                        <MenuItem value="">
                                            <em>Select Account</em>
                                        </MenuItem>
                                        {accounts.map((account) => (
                                            <MenuItem key={account.id} value={account.id.toString()}>
                                                {account.admin_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )} */}
                        {/* {!hasManagerRole && (
                            <Grid item xs={12} sm={6} className="manager-section">
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="manager-label">Manager</InputLabel>
                                    <Select
                                        labelId="manager-label"
                                        name="manager_id"
                                        value={formData.manager_id}
                                        onChange={handleInputChange}
                                        label="Manager"
                                    >
                                        <MenuItem value="">
                                            <em>Select Manager</em>
                                        </MenuItem>
                                        {managers.map((manager) => (
                                            <MenuItem key={manager.id} value={manager.id.toString()}>
                                                {manager.first_name} {manager.last_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )} */}
                        {/* <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="normal"  size="small" >
                                <InputLabel id="gender-label">Gender</InputLabel>
                                <Select
                                    labelId="gender-label"
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    label="Gender"
                                >
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid> 

                        <Grid item xs={6}>
                            <FormControl fullWidth margin="normal"  size="small" >
                                <InputLabel id="job-role-label">Job Role</InputLabel>
                                <Select
                                    labelId="job-role-label"
                                    name="job_role_id"
                                    value={formData.job_role_id}
                                    onChange={handleInputChange}
                                    label="Job Role"
                                >
                                    <MenuItem value="">
                                        <em>Select Job Role</em>
                                    </MenuItem>
                                    {jobRoles.map((jobRole) => (
                                        <MenuItem key={jobRole.id} value={jobRole.id.toString()}>
                                            {jobRole.role_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>*/}

                        {/* Active Fields Section */}
                        {activeFields.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                                    Additional Information
                                </Typography>
                                <Grid container spacing={2}>
                                    {activeFields.map((field) => (
                                        <Grid item xs={12} sm={6} key={field.id}>
                                            <FormControl 
                                                fullWidth 
                                                margin="normal"
                                                sx={{ mb: 2 }}
                                                variant="outlined"
                                            >
                                                <InputLabel id={`field-${field.id}-label`}>
                                                    {field.field_name}
                                                </InputLabel>
                                                
                                                <Select
                                                    sx={{
                                                        "& .MuiOutlinedInput-input": {
                                                        padding: "0px !important",
                                                        }
                                                    }}
                                                    labelId={`field-${field.id}-label`}
                                                    multiple={field.is_multivalued === 1}
                                                    value={fieldValues[field.id] || (field.is_multivalued === 1 ? [] : '')}
                                                    onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
                                                    input={<OutlinedInput label={field.field_name} />}
                                                    renderValue={(selected) => {
                                                        if (field.is_multivalued === 1) {
                                                            return selected.map(valueId => {
                                                                const valueObj = field.values.find(v => v.id.toString() === valueId);
                                                                return valueObj ? valueObj.value : '';
                                                            }).join(', ');
                                                        } else {
                                                            const valueObj = field.values.find(v => v.id.toString() === selected);
                                                            return valueObj ? valueObj.value : '';
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Select {field.field_name}</em>
                                                    </MenuItem>
                                                    {field.values.map((valueObj) => (
                                                        <MenuItem key={valueObj.id} value={valueObj.id.toString()}>
                                                            {field.is_multivalued === 1 && (
                                                                <Checkbox 
                                                                    checked={
                                                                        fieldValues[field.id] ? 
                                                                        fieldValues[field.id].indexOf(valueObj.id.toString()) > -1 : 
                                                                        false
                                                                    } 
                                                                />
                                                            )}
                                                            <ListItemText primary={valueObj.value} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        )}                            
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                    >
                        {userId ? 'Update' : 'Send invite'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default UserFormModal;
